import React, { useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Highcharts from "highcharts";
import StockModule from "highcharts/modules/stock";
import HighchartsReact from "highcharts-react-official";
import hash from "object-hash";
import { injectGlobal } from "styled-components";
import moment from "moment";

import { numberWithCommas } from "utils/textFormatting";

StockModule(Highcharts);

injectGlobal`
  /* this is the chart overlay backdrop */
  g.highcharts-tooltip {
    display:none;
  }

  .apc-highcharts-tooltip {
    border-radius: 6px;
    box-shadow: 0 12px 34px 0 rgba(71, 88, 114, 0.2);
    font-size: 14px;
    background-color: #fff;
    pointer-events: auto;
    font-family: Muli !important;
    min-width: 280px;

    * {
      margin: 0;
    }

    .header {
      background-color: #f5f7fa; /* gray-1 */
      border-radius: 6px 6px 0 0;
      padding: 8px 24px 7px 24px;
      
      font-weight: bold;
      color: #394455;
    }

    .tooltip-body {
      padding: 12px 24px 24px 24px;
    }

    .tooltip-header-row {
      font-weight: bold;
    }

    .tooltip-row {
      overflow: auto;
      padding: 6px 0;

      span {
        float: left;
      
        &:last-child {
          float: right;
          font-weight: bold;
        }
      }
    }

    .tooltip-bullet {
      font-size: 20px;
      line-height: 14px;
      padding-right: 8px;
    }
  }
`;

const Chart = ({
  type,
  series,
  title = false,
  style = { fontFamily: "Muli, Helvetica Neue, sans-serif" },
  padding = 30,
  titleX = "Time",
  titleY = "Count",
  height = 275
}) => {
  const chartRef = useRef(null);

  const options = useMemo(
    () => ({
      chart: { type, height, spacingLeft: -5 },
      credits: {
        enabled: false
      },
      title,
      xAxis: {
        dateTimeLabelFormats: {
          day: "%b %e",
          week: "%b %e",
          month: "%b '%y"
        },
        title: {
          text: titleX,
          style,
          padding
        },
        type: "datetime"
      },
      yAxis: {
        title: {
          text: titleY,
          style,
          padding
        }
      },
      legend: {
        itemStyle: style,
        align: "left",
        verticalAlign: "top",
        symbolHeight: 12,
        symbolWidth: 12,
        symbolRadius: 6,
        margin: padding
      },
      tooltip: {
        shared: true,
        padding: 0,
        formatter: function () {
          /* eslint-disable react/no-this-in-sfc */
          const date = this.points[0].x;
          return `
            <div class="apc-highcharts-tooltip">
              <div class="header">${moment(date)
                .zone(0)
                .format("ddd, MMM D, YYYY")}</div>
              <div class="tooltip-body">
                <div class="tooltip-row tooltip-header-row">
                  <span>Name</span>
                  <span>Total</span>
                </div>

                ${this.points
                  .map(point => {
                    return `
                    <div class="tooltip-row">
                      <span class="tooltip-bullet" style="color:${
                        point.color
                      };">\u2022 </span>
                      <span>${point.series.name}</span>
                      <span>${numberWithCommas(point.y)}</span>
                    </div>
                  `;
                  })
                  .join("")}
              </div>
            </div>
          `;
        },
        useHTML: true
      }
    }),
    []
  );

  useEffect(() => {
    if (chartRef) {
      const {
        current: { chart }
      } = chartRef;
      while (chart.series.length > 0) {
        chart.series[0].remove(false);
      }
      series.forEach(newSeries => chart.addSeries(newSeries));
      chart.redraw();
    }
  }, [chartRef, hash(series)]);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
  );
};

Chart.propTypes = {
  type: PropTypes.string,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      color: PropTypes.string,
      fillOpacity: PropTypes.number,
      type: PropTypes.string,
      data: PropTypes.arrayOf(
        PropTypes.shape({ x: PropTypes.number, y: PropTypes.number })
      )
    })
  ),
  title: PropTypes.string,
  style: PropTypes.object,
  padding: PropTypes.number,
  titleX: PropTypes.string,
  titleY: PropTypes.string,
  height: PropTypes.number
};

export default Chart;
